import React, {useEffect, useState} from 'react';
import styles from './CreateAdminProject.module.css';
import FileInput from '../../common/input/FileInputPanel';
import TextCard from '../../common/textCard/TextCard';
import {useNavigate} from "react-router-dom";
import {ProjectType} from "../../../types/project";
import {useAlert} from "../../common/error/AlertContext";
import {ClientListResponseData, CreateProjectRequest} from "../../../types/admin";
import {createProjectAPI, getClientListAPI} from "../../../services/admin";
import Button from "../../common/button/Button";

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalCreateProjectRequest {
    clientId: number;
    projectName: string;
    workerAmount: number;
    deadline: Date;
    startAt: Date;
    dataAmount: number;
    projectType: ProjectType;
}

// 프로젝트 생성
const RequestProjectForm: React.FC = () => {
    const navigate = useNavigate();
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [availableClients, setAvailableClients] = useState<ClientListResponseData[]>([]);

    // 내부적으로는 Date 객체로 관리
    const [formData, setFormData] = useState<InternalCreateProjectRequest>({
        clientId: 0,
        projectName: '',
        workerAmount: 0,
        deadline: new Date(),
        startAt: new Date(),
        dataAmount: 0,
        projectType: ProjectType.POLYGON
    });

    const {showAlert} = useAlert();

    // Date 객체를 YYYY-MM-DD 문자열로 변환 (로컬 시간대 기준)
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Date 객체를 화면 표시용 문자열로 변환 (로컬 시간대 기준)
    const formatDateForDisplay = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiFormData = (): CreateProjectRequest => ({
        clientId: formData.clientId,
        projectName: formData.projectName,
        workerAmount: formData.workerAmount,
        deadline: formatDateToString(formData.deadline),
        startAt: formatDateToString(formData.startAt),
        dataAmount: formData.dataAmount,
        projectType: formData.projectType
    });

    useEffect(() => {
        const fetchAvailableClients = async () => {
            try {
                //클라이언트 목록 조회 API, 대상 클라이언트 선택용
                const response = await getClientListAPI();
                setAvailableClients(response);
                if(response.length > 0) {
                    setFormData(prevState => ({
                        ...prevState,
                        clientId: response[0].clientId
                    }))
                }
            } catch (error) {
                console.error('Failed to fetch available clients:', error);
            }
        };
        fetchAvailableClients();
    }, []);

    //업로드 가능한 파일 확장자
    const ALLOWED_IMAGE_TYPES = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp'
    ];

    //확장자 검수
    const validateImageFile = (file: File): boolean => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            showAlert('이미지 파일만 업로드 가능습니다.', "error");
            return false;
        }
        return true;
    };

    //파일 수정
    const handleFileSelect = (file: File) => {
        if (!validateImageFile(file)) return;
        setUploadedFile(file);
    };

    //프로젝트 추가 요청 생성
    const handleSubmit = async () => {
        if (!formData.projectName.trim()) {
            showAlert('프로젝트 이름을 입력해주세요.', "error");
            return;
        }
        try {
            // Date 객체를 문자열로 변환하여 전송
            const requestData = getApiFormData();

            //프로젝트 생성 API
            await createProjectAPI(requestData, uploadedFile);
            showAlert("프로젝트 생성이 완료되었습니다", "info");
            //프로젝트 테이블 페이지로 이동
            navigate("/admin/projects");
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    //프로젝트 정보
    const projectDetails = [
        //데이터 수량 선택
        {
            label: "데이터 수량",
            value: formData.dataAmount.toLocaleString(),
            type: "text" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                dataAmount: parseInt(value) || 0
            }))
        },
        //가공방식 선택
        {
            label: "가공 방식",
            value: formData.projectType,
            type: "select" as const,
            options: Object.values(ProjectType),
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                projectType: value as ProjectType
            }))
        }
    ];

    //프로젝트 날짜 정보
    const projectDates = [
        //작업 시작일 선택
        {
            label: "작업 시작일",
            value: formatDateForDisplay(formData.startAt),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                startAt: new Date(value)
            }))
        },
        //작업 종료일 선택
        {
            label: "작업 종료일",
            value: formatDateForDisplay(formData.deadline),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                deadline: new Date(value)
            }))
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Create New Project</h1>
                    <p className={styles.subtitle}>프로젝트 생성</p>
                </div>
                <div className={styles.actions}>
                    {/*요청 버튼*/}
                    <Button
                        variant={"primary"}
                        onClick={handleSubmit}
                        disabled={formData.clientId === 0}
                        title={formData.clientId === 0 ? "클라이언트를 선택해주세요" : ""}
                    >
                        저장
                    </Button>
                    {/*취소 버튼, 프로젝트 테이블 페이지로 이동*/}
                    <Button
                        variant={"secondary"}
                        onClick={() => navigate("/admin/projects")}
                    >
                        취소
                    </Button>
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.leftSection}>
                    <div className={styles.uploadSection}>
                        {/*샘플 데이터 업로드*/}
                        <FileInput
                            onFileSelect={handleFileSelect}
                            accept={ALLOWED_IMAGE_TYPES.join(',')}
                            maxSize={5 * 1024 * 1024}
                            className={styles.fileInput}
                        />
                    </div>

                    <div className={styles.detailsCard}>
                        {/*프로젝트 정보*/}
                        <TextCard items={projectDetails}/>
                        <TextCard items={projectDates}/>
                    </div>
                </div>

                <div className={styles.rightSection}>
                    <div className={styles.inputGroup}>
                        {/*프로젝트명 입력*/}
                        <label>프로젝트 명</label>
                        <input
                            type="text"
                            className={styles.projectNameInput}
                            placeholder="Project Name"
                            value={formData.projectName}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                projectName: e.target.value
                            }))}
                        />
                    </div>
                    <div>
                        <label htmlFor="clientId">대상 클라이언트</label>
                        <select
                            value={formData.clientId}
                            className={styles.client}
                            onChange={(e) => setFormData(prevState => ({
                                ...prevState,
                                clientId: parseInt(e.target.value)
                            }))}
                        >
                            {availableClients.map(option => (
                                <option key={option.clientId} value={option.clientId}>
                                    {option.companyName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.inputGroup}>
                        <label>작업자 수</label>
                        {/*작업자 수 입력*/}
                        <input
                            type="number"
                            className={styles.workerAmountInput}
                            placeholder="작업자 수"
                            value={formData.workerAmount === 0 ? '' : formData.workerAmount}
                            min="0"
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                workerAmount: parseInt(e.target.value)
                            }))}
                        />
                        명
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestProjectForm;